import type { ReactNode } from "react";
import React, { useEffect } from "react";

import { HotkeyManager } from "capitalrx-hotkeys/lib";

import { DISPLAY_NAMES, HOTKEYS_FEATURE_ENABLED, isDevelopmentMode } from "../constants";
import type { HotKeyRootProps } from "../types";
import { HotkeyProvider } from "./HotkeyProvider";
import { createLogger } from "capitalrx-hotkeys/lib/utils/devLogger";
import { useHotkeyPermissions } from "../hooks/useHotkeyPermissions";

const devLogger = createLogger("[HotKeys:Component]");

export function HotKeyRoot({
  children,
  manager: externalManager,
  config,
  storageKey = "capitalrx-hotkeys-prefs",
  validateAccessibility = isDevelopmentMode(),
  enablePerformanceMonitoring = false,
  onHotkeyTrigger,
  onConflict,
}: HotKeyRootProps): ReactNode {
  // LAYER 1: Check build-time environment flag
  const featureEnabled = HOTKEYS_FEATURE_ENABLED;

  // LAYER 2: Check module metadata permissions
  const permissions = useHotkeyPermissions();

  // Determine if hotkeys should be enabled
  const shouldEnableHotkeys = React.useMemo(() => {
    // Layer 1: Feature globally disabled
    if (!featureEnabled) {
      devLogger.info("HotKeys disabled - HOTKEYS_FEATURE_ENABLED is false");
      return false;
    }

    // Layer 2: Module lacks permission. Warn in development only to avoid flooding production telemetry.
    if (!permissions.canRegister) {
      if (isDevelopmentMode()) {
        console.warn(
          `[HotKeys] Module "${permissions.moduleId}" does not have permission to use hotkeys. ` +
            `Add "hotkeys:register" to permissions array in metadata.json or set features.hotkeys.enabled = true.`,
        );
      }
      return false;
    }

    // All checks passed
    devLogger.info(`HotKeys enabled for module "${permissions.moduleId}"`, {
      canRegister: permissions.canRegister,
      canUseGlobalScope: permissions.canUseGlobalScope,
      maxRegistrations: permissions.maxRegistrations,
      allowedScopes: permissions.allowedScopes,
    });
    return true;
  }, [featureEnabled, permissions]);

  // If hotkeys disabled, render children without context
  if (!shouldEnableHotkeys) {
    return <>{children}</>;
  }

  // Hotkeys enabled - render with full functionality
  return <HotKeyRootEnabled {...{ children, manager: externalManager, config, storageKey, validateAccessibility, enablePerformanceMonitoring, onHotkeyTrigger, onConflict }} />;
}

/**
 * Internal component that renders HotKeyRoot with full functionality.
 * Only rendered when all permission checks pass.
 */
function HotKeyRootEnabled({
  children,
  manager: externalManager,
  config,
  storageKey = "capitalrx-hotkeys-prefs",
  validateAccessibility = isDevelopmentMode(),
  enablePerformanceMonitoring = false,
  onHotkeyTrigger,
  onConflict,
}: HotKeyRootProps): ReactNode {
  devLogger.info("HotKeyRoot initialization Details", {
    enablePerformanceMonitoring,
    validateAccessibility,
    config,
    storageKey,
  });

  const [manager] = React.useState(() => {
    if (externalManager) return externalManager;
    return new HotkeyManager(config);
  });

  useEffect(() => {
    if (storageKey && typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const preferences = JSON.parse(stored);
          manager.setPreferences?.(preferences);
        }
      } catch (error) {
        console.warn("Failed to load hotkey preferences:", error);
      }
    }
    return () => {
      if (storageKey && typeof window !== "undefined") {
        try {
          const preferences = manager.getPreferences?.();
          const snapshot = typeof preferences?.getPreferences === "function" ? preferences.getPreferences() : preferences;
          if (snapshot) {
            localStorage.setItem(storageKey, JSON.stringify(snapshot));
          }
        } catch (error) {
          console.warn("Failed to save hotkey preferences:", error);
        }
      }
    };
  }, [manager, storageKey]);

  useEffect(() => {
    if (onHotkeyTrigger) {
      const unsubscribe = manager.on?.(
        "hotkey:triggered",
        (config: { modifiers?: string[]; key: string }, event: { originalEvent: KeyboardEvent }) => {
          const combo = `${config.modifiers?.join("+") || ""}${config.modifiers?.length ? "+" : ""}${config.key}`;
          onHotkeyTrigger(combo, event.originalEvent);
        },
      );
      return unsubscribe;
    }
  }, [manager, onHotkeyTrigger]);

  // Track registry version to detect hotkey changes
  const [registryVersion, setRegistryVersion] = React.useState(0);

  useEffect(() => {
    const handleRegistryChange = () => {
      setRegistryVersion((v) => v + 1);
    };

    const unsubscribeRegister = manager.on?.("hotkey:registered", handleRegistryChange);
    const unsubscribeUnregister = manager.on?.("hotkey:unregistered", handleRegistryChange);

    return () => {
      unsubscribeRegister?.();
      unsubscribeUnregister?.();
    };
  }, [manager]);

  // Validate accessibility only when registry changes (not continuously)
  useEffect(() => {
    if (validateAccessibility) {
      const hotkeys = manager.getHotkeys?.() || [];
      hotkeys.forEach((hotkey: { config: { modifiers?: string[]; key: string } }) => {
        if (hotkey.config.modifiers?.length === 0) {
          console.warn(
            `WCAG Warning: Hotkey "${hotkey.config.key}" has no modifiers. Consider adding at least one modifier key for accessibility.`,
          );
        }
      });
    }
  }, [manager, validateAccessibility, registryVersion]);

  // Check conflicts only when registry changes (not continuously)
  useEffect(() => {
    if (onConflict) {
      const conflicts = manager.checkConflicts?.() || [];
      if (conflicts.length > 0) {
        onConflict(conflicts);
      }
    }
  }, [manager, onConflict, registryVersion]);

  return <HotkeyProvider manager={manager}>{children}</HotkeyProvider>;
}

HotKeyRoot.displayName = DISPLAY_NAMES.HotKeyRoot;
